import { BLC } from '@/data';
import type { Unit, Pixel } from '@/data';
import { pageToPageData } from '@/http-client/utils/pageToPageData';
import { getGeneratedPositions } from '@/http-client/renderServices/StructureService';
import type { Image3DPageData } from '@/data/sources';
import MetadataParser from '../MetadataParser';
import type { Image3DPageFormat, Image3DRowFormat, Image3DSyncFormat } from '../UnitParser';
import UnitParser from '../UnitParser';
import { Structure } from '../../data/Structure';
import type { StructureFormat } from './StructureFormat';

export default class StructureParser {
  static structureFormatFromNewFormat(sourceFormat: Map<string, unknown>): StructureFormat {
    return {
      info: sourceFormat.get('text') as string,
      license: sourceFormat.get('license') as string,
      contributors: sourceFormat.get('contributors') as Record<string, string>,
      id: sourceFormat.get('id') as string,
      name: sourceFormat.get('name') as string,
      year: sourceFormat.get('year') as string,
    };
  }

  static fromFormat(
    format: StructureFormat,
  ): Structure {
    const name = format?.name;

    const id = format?.id;

    const positions: Image3DSyncFormat = getGeneratedPositions();
    const pixelUnit: Unit<Pixel> = UnitParser.createImage3DUnit(positions);

    const pagesDimensions: { height: Pixel; width: Pixel; }[] = [];

    const pages = positions.pages
      .map((page) => StructureParser.createNewPage(page, pagesDimensions));

    const maxWidth = pagesDimensions
      .map((dim) => dim.width as number)
      .reduce((prev, curr) => Math.max(prev, curr));

    const totalHeight = pagesDimensions
      .map((dim) => dim.height as number)
      .reduce((prev, curr) => prev + curr + BLC.rowHeight);

    const metadata = MetadataParser.fromFormat(format);

    const oldRows = positions.pages[0].rows;
    const svgImage = StructureParser.generateSvgFromRows(oldRows);
    const easterEgg = `<text id="easterEgg" x="${maxWidth}" y="${totalHeight}" >_ |</text>`;

    return new Structure({
      name,
      id,
      pixelUnit,
      width: maxWidth as Pixel,
      height: totalHeight as Pixel,
      pages,
      metadata,
      svgImage: svgImage + easterEgg,
    });
  }

  private static createNewPage(
    oldPage: Image3DPageFormat,
    pagesDimensions: { height: Pixel; width: Pixel;}[],
  ): Image3DPageData {
    const maxWidth: Pixel = StructureParser.calculateMaxWidth(oldPage);
    const totalHeight: Pixel = StructureParser.calculateTotalHeight(oldPage);

    const dimensions = { height: totalHeight as Pixel, width: maxWidth };

    pagesDimensions.push(dimensions);

    return pageToPageData(oldPage, dimensions);
  }

  private static calculateMaxWidth(page: Image3DPageFormat): Pixel {
    const widthMaxInRows = page.rows
      .map((row) => row['end-x'])
      .reduce((prev, curr) => Math.max(prev, curr));

    return widthMaxInRows as Pixel;
  }

  private static calculateTotalHeight(page: Image3DPageFormat) {
    const lastRow = page.rows.at(-1);
    const lastY = lastRow?.y || 0;
    const lastHeight = lastRow?.height || 0;

    return (lastY + lastHeight) as Pixel;
  }

  private static generateSvgFromRows(
    rows: Image3DRowFormat[],
  ) {
    return rows
      .map(StructureParser.getLinesInfoToGenerateSVG)
      .map((value) => StructureParser.generateLine(
        value.xList,
        value.y,
        value.endX,
      ))
      .join('<!-- LINE -->');
  }

  private static getXListOf(row: Image3DRowFormat): number[] {
    return row['onset-x'].map((XOnset) => XOnset.x);
  }

  private static getLinesInfoToGenerateSVG(row: Image3DRowFormat) {
    const lastXOnsetIndex = row['onset-x'].length - 1;
    const endX = row['onset-x'][lastXOnsetIndex].x;
    return {
      xList: StructureParser.getXListOf(row),
      y: row.y,
      endX,
    };
  }

  private static generateLine(xList: number[], yLine:number, endX: number) {
    return xList.map((x, index, array) => {
      const nextX = array[index + 1] || endX;
      const width = nextX - x;
      if (width !== 0) {
        return StructureParser.generateCell(x, yLine, width, BLC.rowHeight);
      }
      return '';
    }).join('');
  }

  private static generateCell(x: number, y:number, width: number, height: number): string {
    const verticalPadding = 0;
    const xShiftToAlignWithLabels = 0;
    return `<rect x="${x - xShiftToAlignWithLabels}" y="${y - verticalPadding}" width="${width}" height="${height + 2 * verticalPadding}" class="structure"></rect>`;
  }
}
